import { mountingToManufacturereMap } from 'projectSections/sections/design/systems/tabs/mounting/selector/MountingSelector'
import { otherRowModeSelectKey } from 'resources/systemTemplates/utils/otherComponentMode'
import { StudioSystemType } from 'types/global'
import {
  TEMPLATE_EXTRACT_QUANTITY_DEFAULT_FIXED,
  TEMPLATE_EXTRACT_QUANTITY_DEFAULT_SCALED,
} from './constants'
import { SystemTemplate } from './types'

function parseOptionalNumericField(value: unknown): number | undefined {
  if (value == null || value === '') {
    return undefined
  }
  const n = Number(value)
  return Number.isNaN(n) ? undefined : n
}

function normalizedQuantityForExtract(comp: { quantity?: unknown }, isScaledRow: boolean): number {
  const fallback = isScaledRow
    ? TEMPLATE_EXTRACT_QUANTITY_DEFAULT_SCALED
    : TEMPLATE_EXTRACT_QUANTITY_DEFAULT_FIXED
  const parsed = Number(comp.quantity ?? fallback)
  return Number.isFinite(parsed) ? parsed : fallback
}

function mapComponentQuantityForExtract(comp: any): Record<string, any> {
  const quantityMode = otherRowModeSelectKey(comp.quantity_mode)
  const isScaledRow = Boolean(quantityMode)

  const row: Record<string, any> = {
    code: comp.code,
    quantity: normalizedQuantityForExtract(comp, isScaledRow),
    component_type: comp.component_type || 'module',
  }

  if (isScaledRow) {
    row.quantity_mode = quantityMode
  }

  const modeStep = parseOptionalNumericField(comp.mode_step)
  if (modeStep !== undefined) {
    row.mode_step = modeStep
  }

  const quantityMultiplier = parseOptionalNumericField(comp.quantity_multiplier)
  if (quantityMultiplier !== undefined) {
    row.quantity_multiplier = quantityMultiplier
  }

  const modeCodeTrimmed = comp.mode_code != null ? String(comp.mode_code).trim() : ''
  if (modeCodeTrimmed !== '') {
    row.mode_code = modeCodeTrimmed
  }

  return row
}

export function extractTemplateData(template: SystemTemplate) {
  const components_and_quantities = (template.components_and_quantities?.map((comp: any) =>
    mapComponentQuantityForExtract(comp)
  ) ||
    template.component_type_definitions?.map((comp: any) => ({
      code: comp.code,
      quantity: comp.quantity || 1,
      component_type: comp.code.includes('BATT')
        ? 'battery'
        : comp.hybrid === 'Y' || comp.inverter_id
          ? 'inverter'
          : 'module',
    })) ||
    []) as any

  const formattedConstraints = (template.constraints || []).map((constraint: any) => ({
    type: constraint.type || constraint.constraint_type,
    min: constraint.min ?? constraint.minimum ?? null,
    max: constraint.max ?? constraint.maximum ?? null,
  }))

  const design_type = template.settings?.design_type || template.design_type || 'set_annual_kwh_energy_offset'
  const orientationOptions = template.settings?.orientation_options ?? template.orientation_options ?? null
  const azimuth = template.settings?.azimuth_from_to ?? template.azimuth_from_to

  const inverter_design_assistant = template.inverter_design_assistant?.codes?.length
    ? {
        inverter_type: (template.inverter_design_assistant as any).inverter_type || 'string',
        codes: template.inverter_design_assistant.codes,
      }
    : null

  const battery_design_assistant = template.battery_design_assistant?.codes?.length
    ? {
        customer_goal: (template.battery_design_assistant as any).customer_goal || 'self_consumption',
        backup_hours: (template.battery_design_assistant as any).backup_hours || 0,
        continuous_power: (template.battery_design_assistant as any).continuous_power || 0,
        peak_power: (template.battery_design_assistant as any).peak_power || 0,
        codes: template.battery_design_assistant.codes,
      }
    : null

  return {
    name: template.name,
    description: template.description || '',
    components_and_quantities,
    constraints: formattedConstraints,
    design_type,
    orientation_options: orientationOptions,
    azimuth_from_to:
      Array.isArray(azimuth) &&
      azimuth.length === 2 &&
      azimuth.every((v) => typeof v === 'number' && v >= 0 && v <= 360)
        ? azimuth
        : null,
    inverter_design_assistant,
    battery_design_assistant,
    pricing_scheme_id: template.pricing_scheme_id,
    mounting_config: template.mounting_config,
    payment_options_override: template.payment_options_override,
    incentives_override: template.incentives_override,
    costing_override: template.costing_override,
    commission_override_id: template.commission_override_id,
    battery_control_scheme_override: template.battery_control_scheme_override,
  }
}

export const createTemplateDataForOrg = (template: SystemTemplate, orgId: number) => {
  const apiBaseUrl = window.API_BASE_URL
  const extracted = extractTemplateData(template)

  const settingsObject: any = {
    enable_design_rules: false,
    design_type: extracted.design_type,
    battery_design_assistant_enabled: !!extracted.battery_design_assistant?.codes?.length,
    inverter_design_assistant_enabled: !!extracted.inverter_design_assistant?.codes?.length,
    autoApplyKeys: {
      mounting: !!extracted.mounting_config,
      pricing_scheme: !extracted.pricing_scheme_id,
      incentives_override: !extracted.incentives_override?.length,
      payment_options_override: !extracted.payment_options_override?.length,
      costing_override: !extracted.costing_override,
      commission_override: !extracted.commission_override_id,
      battery_control_scheme_override: !extracted.battery_control_scheme_override,
    },
    selectedPricingScheme: { pricing_formula: '' },
    inverter_selection_mode: extracted.inverter_design_assistant?.codes?.length ? 'enabled' : 'disabled',
    battery_selection_mode: extracted.battery_design_assistant?.codes?.length ? 'enabled' : 'disabled',
    components_and_quantities: extracted.components_and_quantities,
    constraints: extracted.constraints,
  }

  if (extracted.orientation_options !== null && extracted.orientation_options !== '') {
    settingsObject.orientation_options = extracted.orientation_options
  }

  if (extracted.azimuth_from_to) {
    settingsObject.azimuth_from_to = extracted.azimuth_from_to
  }

  if (extracted.inverter_design_assistant) {
    settingsObject.inverter_design_assistant = extracted.inverter_design_assistant
  }

  if (extracted.battery_design_assistant) {
    settingsObject.battery_design_assistant = extracted.battery_design_assistant
  }

  return {
    name: extracted.name,
    description: extracted.description,
    calculator_id: 0,
    settings: JSON.stringify(settingsObject),
    pricing_scheme: extracted.pricing_scheme_id
      ? `${apiBaseUrl}orgs/${orgId}/pricing_schemes/${extracted.pricing_scheme_id}/`
      : '',
    incentives_override: extracted.incentives_override
      ? extracted.incentives_override.map((id) => `${apiBaseUrl}orgs/${orgId}/incentives/${id}/`)
      : [],
    payment_options_override: extracted.payment_options_override
      ? extracted.payment_options_override.map((id) => `${apiBaseUrl}orgs/${orgId}/payment_options/${id}/`)
      : [],
    costing_override: extracted.costing_override
      ? `${apiBaseUrl}orgs/${orgId}/costings/${extracted.costing_override}/`
      : '',
    commission_override: extracted.commission_override_id
      ? `${apiBaseUrl}orgs/${orgId}/commissions/${extracted.commission_override_id}/`
      : '',
    battery_control_scheme_override: extracted.battery_control_scheme_override || '',
    mounting_config: extracted.mounting_config ?? null,
  }
}

export const createTemplateFromSystem = (
  system: StudioSystemType,
  name: string,
  description: string
): SystemTemplate => {
  if (!system) {
    throw new Error('System is required to create template')
  }
  const mountingType = system.mounting
  const mountingManufacturer = mountingType ? mountingToManufacturereMap[mountingType] : undefined

  const moduleTypes = system.getModuleTypes ? system.getModuleTypes() : []
  const inverters = system.inverters ? system.inverters() : []
  const batteries = system.batteries ? system.batteries() : []

  const others = system.others
    ? system.others().filter((o) => o.manufacturer_name?.toLowerCase() !== mountingManufacturer?.toLowerCase())
    : []

  const componentQuantities = [
    ...moduleTypes.map((moduleType: any) => ({
      code: moduleType.code,
      quantity: moduleType.quantity || 1,
      component_type: 'module',
    })),
    ...inverters.map((inverter: any) => ({
      code: inverter.code,
      quantity: inverter.quantity || 1,
      component_type: 'inverter',
    })),
    ...batteries.map((battery: any) => ({
      code: battery.code,
      quantity: battery.quantity || 1,
      component_type: 'battery',
    })),
    ...others.map((other: any) => ({
      code: other.code,
      quantity: other.quantity || 1,
      component_type: 'other',
    })),
  ]

  let designType: SystemTemplate['design_type'] = 'set_module_qty'

  const moduleQty = system.moduleQuantity() || 0

  const systemSizeConstraint = {
    type: 'module_quantity',
    min: 1,
    max: moduleQty,
  }

  const constraints = [systemSizeConstraint]

  const inverterDesignAssistant = {
    codes: Array.from(new Set(inverters.map((inverter: any) => inverter.code))).join(','),
    inverter_type: inverters[0]?.inverter_type || 'string',
  }

  const mounting_config = {
    mounting_id: mountingType,
  }

  return {
    id: -1,
    name: name,
    description: description,
    design_type: designType,
    orientation_options: null,
    azimuth_from_to: null,
    constraints: constraints,
    settings: {
      design_type: designType,
      orientation_options: null,
      azimuth_from_to: null,
      constraints: constraints,
      components_and_quantities: componentQuantities,
    },
    components_and_quantities: componentQuantities,
    mounting_config: mounting_config,
    battery_design_assistant: null,
    inverter_design_assistant: inverterDesignAssistant,
    commission_override_id: system.commission_id,
    costing_override: system.costing_override,
    incentives_override: system.incentives || [],
    payment_options_override: system.payment_options_override,
    battery_control_scheme_override: system.battery_control_scheme,
    pricing_scheme_id: system.pricing_scheme_id,
  }
}
